import { Button, Card, CardContent, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { displayNotification, formTypeCheck } from "helpers";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataElement from "modules/pvs_activities/components/preview/DataElement";
import { FaChevronLeft, FaChevronRight, FaLock, FaSave, FaUnlock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchReportLists } from "../redux/reportSlice";
import { FormDataContext } from "./contexts/FormContext";
import { useCreateOrUpdateSingleFormDataMutation } from "../redux/service/formData";
import { useSubmitDFormMutation } from "../redux/service/dForm";
import { useGetElementsQuery } from "../redux/service/elements";
import { DFORM_ASSIGNED_FOR, MODULE_PREFIX, REPORT_ASSIGNMENT_STATUS } from "../constants";
import CommentQuestionMatrix from "./CommentQuestionMatrix";
import AttachmentMatrix from "./AttachmentMatrix";
import ViewTaskHistoryModal from "./preview/modals/ViewTaskHistoryModal";
import { useGetFormSectionQuery, useGetFormSectionsQuery } from "../redux/service/formSections";
import Instructions from "./Instructions";
import SubmitReportModal from "./preview/modals/SubmitReportModal";
import CommentIcon from "@mui/icons-material/Comment";
import AttachmentIcon from "@mui/icons-material/Attachment";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Tooltip } from "@mui/material";
import { processDataElementValue } from "../helper.general";

const defaultProps = {
    url: "",
    templateId: "",
    sectionId: "",
    form: {
        id: "",
        name: "",
        layout_ref: "",
    },
    renderHeader: () => { },
    isPopupForm: false,
    onSubmit: () => { },
};

const FormEngine = (rawProps) => {
    const { url, templateId, sectionId, form, movementId, renderHeader, isPopupForm, onSubmit } = {
        ...defaultProps,
        ...rawProps,
    };
    const location = useLocation();
    const surveyMissionId = location.state?.surveyMissionId;

    const authData = useSelector((s) => s.auth.data);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const siteLanguageKey = useSelector((s) => s.general.siteLanguageSF);
    const formDataContext = useContext(FormDataContext);

    const [createOrUpdateSingleFormData] = useCreateOrUpdateSingleFormDataMutation();
    const [submitDForm, dFormSubmitRes] = useSubmitDFormMutation();
    const elementsRes = useGetElementsQuery(
        { sectionId, movementId: formDataContext.movementId },
        { skip: ["cm", "qm", "am", "", undefined, null].includes(sectionId) }
    );
    const sectionsRes = useGetFormSectionsQuery({
        templateId,
        params: {
            "include-elements": "false",
            "all-sections": "true",
        },
    });
    const sectionRes = useGetFormSectionQuery(sectionId, {
        skip: ["cm", "qm", "am", "", undefined, null].includes(sectionId),
    });
    const [isLocked, setIsLocked] = useState(false);

    const [activeMatrix, setActiveMatrix] = useState(false);

    const handleTab = (event, newValue) => {
        setActiveMatrix(newValue);
        navigate(`${url}/${newValue}`, { replace: true });
    };

    useEffect(() => {
        // loadSectionsData();
        dispatch(fetchReportLists({ mode: "template", id: templateId }));
    }, []);

    useEffect(() => {
        if (!["cm", "qm", "am", "", undefined, null].includes(sectionId)) {
        } else {
            // setTabs([]);
        }
    }, [sectionId]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (sectionsRes.isError) {
            displayNotification("error", "Error loading Form Parts");
            console.error({ error: sectionsRes.error });
        }
    }, [sectionsRes]);

    useEffect(() => {
        if (sectionRes.isError) {
            displayNotification("error", "Error loading Form Part Data");
            console.error({ error: sectionRes.error });
        }
    }, [sectionRes]);

    useEffect(() => {
        if (elementsRes?.status !== "uninitialized") {
            if (elementsRes?.isError) {
                displayNotification("error", "Error loading Heading Elements");
                console.error({ error: elementsRes.error });
            }
            if (elementsRes.status === "fulfilled" && elementsRes.isSuccess && !elementsRes.isLoading) {
                if (formDataContext.mode === "preview") {
                    const tmp = {};
                    for (const x of elementsRes.data) {
                        tmp[x.id] = { id: x.id, content: "" };
                    }
                    formDataContext.setElementsData((existing) => ({
                        ...existing,
                        ...tmp,
                    }));
                }
            }
        }
    }, [elementsRes]);

    const sections = useMemo(() => {
        if (sectionsRes.isSuccess) return sectionsRes.data;
        return [];
    }, [sectionsRes]);

    const fixedSections = useMemo(() => {
        if (sectionsRes.isSuccess) return sectionsRes.data.filter((r) => r.is_fixed);
        return [];
    }, [sectionsRes]);

    const nonFixedSections = useMemo(() => {
        if (sectionsRes.isSuccess) return sectionsRes.data.filter((r) => r.is_fixed === 0);
        return [];
    }, [sectionsRes]);

    const section = useMemo(() => {
        if (sectionRes.isSuccess) return sectionRes.data;
        return [];
    }, [sectionRes]);

    const elements = useMemo(() => {
        if (elementsRes.isSuccess) return elementsRes.data;
        return [];
    }, [elementsRes]);

    const handleChange = async (elId, val) => {
        try {
            if (formDataContext.mode === "preview") {
                formDataContext.setElementsData((existing) => ({
                    ...existing,
                    [elId]: {
                        ...existing[elId],
                        content: val,
                    },
                }));
            } else {
                const res = await createOrUpdateSingleFormData({
                    template_id: formDataContext.templateId,
                    section_id: formDataContext.sectionId,
                    element_id: elId,
                    movement_id: formDataContext.movementId,
                    ...(Array.isArray(val)
                        ? { entries: val }
                        : {
                            content: typeof val === "object" ? val?.content : val,
                            list_id: typeof val === "object" ? val?.list_id : null,
                            list_element_id: typeof val === "object" ? val?.list_element_id : null,
                        }),
                }).unwrap();
                if (res.success === false) {
                    displayNotification("error", "Error Saving Element Data [B]");
                }
            }
        } catch (e) {
            console.error({ e });
            displayNotification("error", "Error Saving Element Data [F]");
        }
    };

    const handleSaveAndClose = () => {
        let tempURL = `/portal/${MODULE_PREFIX}/template-library`;
        const check = localStorage.getItem(`pvsa-process-return-url`);
        if (check !== null) {
            tempURL = check;
            localStorage.removeItem(`pvsa-process-return-url`);
        }
        navigate(tempURL);
    };

    const handleSubmitClick = (isValidated, submission_for = null, trigger_assignment = null) => {
        const check = localStorage.getItem(`pvsa-process-return-url`) ?? '/portal/mission-cc';
        const submitCallback = () => {
            if (movementId && window.confirm("You are about to submit. Are you sure?")) {
                let typ = "";
                const userId = authData.id;
                if (formDataContext.formType === "FT_RPT") {
                    typ = "report";
                }
                submitDForm({
                    templateId,
                    movementId,
                    typ,
                    isValidated,
                    userId,
                    ...(submission_for ? { submission_for } : {}),
                    ...(trigger_assignment ? { trigger_assignment } : {}),
                }).then((response) => {
                    if (response.error) {
                        displayNotification("error", "An error occured when submitting");
                    } else {
                        displayNotification("success", response.data.message);
                        if (isPopupForm === false) {
                            navigate(check);
                            localStorage.removeItem(`pvsa-process-return-url`);
                        }
                    }
                });
            }
        };
        if (isPopupForm) onSubmit(submitCallback);
        else submitCallback();
    };

    const currentSectionIndex = sections.findIndex((r) => parseInt(r.id) === parseInt(sectionId));
    const previousSectionIndex = currentSectionIndex - 1;
    const nextSectionIndex = currentSectionIndex + 1;
    const previousSectionId = sections[previousSectionIndex]?.id ?? 0;
    const nextSectionId = sections[nextSectionIndex]?.id ?? 0;
    const finalSectionIndex = sections.length - 1;

    const handleGoPreviousSection = () => {
        if (![-1, 0].includes(currentSectionIndex)) {
            navigate(`${url}/${previousSectionId}`, { replace: true }, { state: { surveyMissionId } });
        }
    };

    const handleGoNextSection = () => {
        if (![-1, finalSectionIndex].includes(currentSectionIndex)) {
            navigate(`${url}/${nextSectionId}`, { state: { surveyMissionId } });
        }
    };

    const renderSectionHeader = (
        sectionName = "Section Name",
        sectionDescription = "",
        hasInstructions = false,
        instructionsContent = ""
    ) => (
        <>
            <div style={{ background: "#edece3", padding: "30px" }}>
                <Typography
                    variant={"h3"}
                    sx={{ mb: 1, fontWeight: "bold", letterSpacing: "0.1rem" }}
                    className="card-title-1"
                >
                    {sectionName}
                </Typography>
                {sectionDescription !== "" && <div dangerouslySetInnerHTML={{ __html: sectionDescription }} />}
            </div>
            {hasInstructions === true && instructionsContent && <Instructions>{instructionsContent}</Instructions>}
        </>
    );

    const renderReportButtons = (dform_assigned_for) => {
        return (
            <>
                {REPORT_ASSIGNMENT_STATUS.IN_DRAFT === parseInt(dform_assigned_for) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        className="ms-2 my-auto h-100"
                        onClick={() => handleSubmitClick(false, null, true)}
                    >
                        Ready for Peer Review
                    </Button>
                )}
                {REPORT_ASSIGNMENT_STATUS.IN_PEER_REVIEW === parseInt(dform_assigned_for) && (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            className="ms-2 my-auto h-100"
                            onClick={() => handleSubmitClick(false, REPORT_ASSIGNMENT_STATUS.IN_DRAFT, true)}
                        >
                            Submit Comments to Expert Team
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            className="ms-2 my-auto h-100"
                            onClick={() => handleSubmitClick(false, null, true)}
                        >
                            Submit Report
                        </Button>
                    </>
                )}
                {REPORT_ASSIGNMENT_STATUS.PEER_REVIEWED_EVALUATION === parseInt(dform_assigned_for) && (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            className="ms-2 my-auto h-100"
                            onClick={() => handleSubmitClick(false, REPORT_ASSIGNMENT_STATUS.IN_DRAFT, true)}
                        >
                            Submit back to Team Leader
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className="ms-2 my-auto h-100"
                            onClick={() => handleSubmitClick(false, null, true)}
                        >
                            Ready for Delegate
                        </Button>
                    </>
                )}
                {REPORT_ASSIGNMENT_STATUS.UNDER_DELEGATE_REVIEW === parseInt(dform_assigned_for) && (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            className="ms-2 my-auto h-100"
                            onClick={() =>
                                handleSubmitClick(false, REPORT_ASSIGNMENT_STATUS.UNDER_DELEGATE_REVIEW, true)
                            }
                        >
                            Submit back to Mission Manager
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            className="ms-2 my-auto h-100"
                            onClick={() => handleSubmitClick(false, null, true)}
                        >
                            Mark Report as Accepted
                        </Button>
                    </>
                )}
            </>
        );
    };

    const renderBody = () => {
        const renderContent = (
            <>
                {renderSectionHeader(
                    <div className="d-flex justify-content-between">
                        {section.show_number == 1 && (
                            <span className="me-2 card-title-1" style={{ color: "#bea361" }}>
                                PART {section.number}:
                            </span>
                        )}
                        <span
                            className="card-title-1"
                        //  style={{ color: "#bea361" }}
                        >
                            {section.name}
                        </span>
                        {form.properties?.show_history_button === "1" ? (
                            <ViewTaskHistoryModal
                                className="ms-2 my-auto h-100"
                                movementId={formDataContext.movementId}
                                templateId={formDataContext.templateId}
                                title={form.properties?.history_button_text ?? undefined}
                            />
                        ) : null}
                    </div>,
                    section.description,
                    true,
                    siteLanguageKey === "en"
                        ? section.instructions
                        : siteLanguageKey === "fr"
                            ? section?.properties?.instructions_fr
                            : siteLanguageKey === "es"
                                ? section?.properties?.instructions_es
                                : ""
                )}
                {elementsRes.isLoading || elementsRes.isFetching || elementsRes.status === "pending" ? (
                    <>
                        <Skeleton variant="rounded" width={"100%"} height={80} sx={{ my: 1 }} />
                        <Skeleton variant="rounded" width={"100%"} height={80} sx={{ mb: 1 }} />
                        <Skeleton variant="rounded" width={"100%"} height={80} sx={{ mb: 1 }} />
                    </>
                ) : (
                    elements.map((el) => (
                        <DataElement
                            key={el.id}
                            {...el}
                            templateId={templateId}
                            elementsData={formDataContext.elementsData}
                            value={processDataElementValue(
                                el.data_element_type,
                                el.id,
                                el.list_appearance,
                                el.multiple_selection,
                                formDataContext.elementsData,
                                el?.properties?.default_value ?? ""
                            )}
                            onChange={handleChange}
                            isReadOnly={formDataContext.isReadOnly}
                            defaultData={el?.properties?.default_value ?? ""}
                            surveyMissionId={surveyMissionId ?? ''}
                        />
                    ))
                )}
                <div className="row">
                    <div className="col-md-6 col-sm-6 col-12">
                        {sections.length > 0 && ![-1, 0].includes(currentSectionIndex) ? (
                            <Button
                                variant="contained"
                                color="black"
                                onClick={handleGoPreviousSection}
                                className="me-2 my-auto h-100"
                            >
                                <FaChevronLeft className="me-1" />
                                <span>Previous</span>
                            </Button>
                        ) : null}
                    </div>
                    <div className="col-md-6 col-sm-6 col-12 text-end">
                        {formDataContext.mode === "data_entry" &&
                            (parseInt(form?.movement?.status) === 6 || formDataContext.isReadOnly === false) &&
                            isPopupForm === false &&
                            form.can_submit === true && (
                                <>
                                    {form?.can_save ? (
                                        <Button
                                            variant="contained"
                                            color="black"
                                            onClick={handleSaveAndClose}
                                            className="ms-2 my-auto mx-2 h-100"
                                        >
                                            <FaSave className="me-1" />
                                            <span>Save & Close</span>
                                        </Button>
                                    ) : null}
                                    {sections.length > 0 && currentSectionIndex === finalSectionIndex ? (
                                        <>
                                            {/* <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            handleSubmitClick(
                              parseInt(form?.movement?.dform_assigned_for) ===
                              DFORM_ASSIGNED_FOR.approval,
                              null,
                              false
                            )
                          }
                          className="ms-2 my-auto h-100"
                        >
                          <FaSave className="me-1" />
                          <span>Submit</span>
                        </Button> */}
                                            {/* TODO: Need to implement functionality for report buttons */}
                                            {formTypeCheck(form.type_ref, { report: true }) ? (
                                                <SubmitReportModal
                                                    className="ms-2 my-auto h-100"
                                                    templateTypeName={form?.type_name ?? "FT_FRM"}
                                                    subStatus={form?.movement?.dform_assigned_for}
                                                    subStatusName={form?.movement?.dform_assigned_for_ref ?? "-"}
                                                    templateId={templateId}
                                                    movementId={movementId}
                                                    isPopupForm={isPopupForm}
                                                    onSubmit={onSubmit}
                                                />
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleSubmitClick(false, null, false)}
                                                    className="ms-2 my-auto h-100"
                                                >
                                                    <FaSave className="me-1" />
                                                    <span>Submit</span>
                                                </Button>
                                            )}
                                        </>
                                    ) : null}
                                </>
                            )}
                        {sections.length > 0 && ![-1, finalSectionIndex].includes(currentSectionIndex) ? (
                            <Button
                                variant="contained"
                                color="black"
                                onClick={handleGoNextSection}
                                className="me-2 my-auto h-100"
                            >
                                <FaChevronRight className="me-1" />
                                <span>Next</span>
                            </Button>
                        ) : null}
                    </div>
                </div>
            </>
        );
        return isPopupForm ? (
            renderContent
        ) : (
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2 }}>{renderContent}</CardContent>
            </Card>
        );
    };

    return (
        !["", null, undefined].includes(sectionId) && (
            <div className={`${isPopupForm ? "" : "bg-light-3 flex-grow-1"} py-4`}>
                <div className="container">
                    {renderHeader(() => (
                        <>
                            {isPopupForm === false && (
                                <>
                                    <Tabs value={activeMatrix} TabIndicatorProps={{ style: { display: "none" }, }} onChange={(handleTab)}>
                                        {form.type_ref == "FT_RPT" && (
                                            <Tooltip title="Comments Matrix" placement="top" arrow>
                                                <Tab icon={<CommentIcon />} value="cm" sx={{ color: activeMatrix === "cm" ? "#ff4815" : "grey", }} />
                                            </Tooltip>
                                        )}
                                        {form.type_ref == "FT_RPT" && (
                                            <Tooltip title="Questions Matrix" placement="top" arrow>
                                                <Tab icon={<QuestionMarkIcon />} value="qm" sx={{ color: activeMatrix === "qm" ? "#ff4815" : "grey", }} />
                                            </Tooltip>
                                        )}
                                        {(form.type_ref == "FT_RPT" || form.properties.show_attachment_matrix_in_form === "1") && (
                                            <Tooltip title="Attachments Matrix" placement="top" arrow>
                                                <Tab icon={<AttachmentIcon />} value="am" sx={{ color: activeMatrix === "am" ? "#ff4815" : "grey", }} />
                                            </Tooltip>
                                        )}
                                    </Tabs>
                                    {form.type_ref == "FT_RPT" && (
                                        <select
                                            className="form-control form-control-sm w-25 my-auto ms-2"
                                            // style={tabs.length === 0 ? { marginBottom: "5px" } : {}}
                                            value={sectionId}
                                            onChange={(e) => navigate(`${url}/${e.target.value}`, { replace: true })}
                                        >
                                            <option value="">Select Special Part</option>
                                            {fixedSections.length > 0 ? (
                                                <optgroup label="Report Area">
                                                    {fixedSections.map((r) => (
                                                        <option value={r.id} key={r.id}>
                                                            {parseInt(r.show_number) === 1
                                                                ? `PART ${r.number}: ${r.name}`
                                                                : r.name}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ) : null}
                                        </select>
                                    )}
                                    {/* {formDataContext.isReadOnly === false && form.type_ref == "FT_RPT" && (
                      <Button
                        variant="contained"
                        color="black"
                        className="ms-2 my-auto h-100"
                        onClick={() => {
                          setIsLocked(!isLocked);
                          displayNotification(
                            "success",
                            `Successfully ${!isLocked ? "Locked" : "Unlocked"} Part.`
                          );
                        }}
                      >
                        {isLocked ? (
                          <>
                            <FaUnlock className="me-1" />
                            <span>Unlock</span>
                          </>
                        ) : (
                          <>
                            <FaLock className="me-1" />
                            <span>Lock</span>
                          </>
                        )}
                      </Button>
                    )} */}
                                </>
                            )}
                        </>
                    ))}
                    {nonFixedSections.length > 1 ? (
                        <Card sx={{ mb: 1 }}>
                            <Tabs
                                value={nonFixedSections.map((r) => r.id).includes(sectionId) ? sectionId : false}
                                onChange={(e, newValue) => { navigate(`${url}/${newValue}`, { state: { surveyMissionId } }, { replace: true }, setActiveMatrix(false)) }}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                {nonFixedSections.map((r) => (
                                    <Tab
                                        value={r.id}
                                        key={r.id}
                                        label={parseInt(r.show_number) === 1 ? `PART ${r.number}: ${r.name}` : r.name}
                                    />
                                ))}
                            </Tabs>
                        </Card>
                    ) : null}
                    {["cm", "qm"].includes(sectionId) ? (
                        <CommentQuestionMatrix sectionId={sectionId} renderSectionHeader={renderSectionHeader} />
                    ) : sectionId === "am" ? (
                        <AttachmentMatrix sectionId={sectionId} renderSectionHeader={renderSectionHeader} />
                    ) : (
                        <>{renderBody()}</>
                    )}
                </div>
            </div>
        )
    );
};

export default FormEngine;
